import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import moment from 'moment';
import {YMD_FORMAT, YMD_FORMAT_SHORT} from '../../../core/constants/date-and-time-formats.constants';
import {INVALID_DATE} from '../../../core/constants/validation-error-strings.constants';

@Component({
  selector: 'app-input-date',
  templateUrl: './input-date.component.html',
  styleUrls: ['./input-date.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: InputDateComponent,
    multi: true
  }]
})

export class InputDateComponent implements OnInit, ControlValueAccessor {
  @ViewChild('monthElement') monthElement: ElementRef;
  @ViewChild('yearElement') yearElement: ElementRef;

  readonly MAX_DAY_MONTH_SYMBOLS = 2;
  readonly MAX_YEAR_SYMBOLS = 4;

  INVALID_DATE = INVALID_DATE;

  form: UntypedFormGroup;

  get day(): AbstractControl {
    return this.form.get('day');
  }

  get month(): AbstractControl {
    return this.form.get('month');
  }

  get year(): AbstractControl {
    return this.form.get('year');
  }

  get validDate(): boolean {
    return moment(`${this.year.value}-${this.month.value}-${this.day.value}`, YMD_FORMAT, true).isValid();
  }

  get invalidDate(): boolean {
    return this.day.touched && this.month.touched && this.year.touched && (!this.form.valid || !this.validDate);
  }

  constructor(private formBuilder: UntypedFormBuilder) { }

  ngOnInit(): void {
    this.createForm();
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      day: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
      month: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
      year: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
    });

    this.day.valueChanges.subscribe(value => this.checkValue(this.day, value, this.monthElement));
    this.month.valueChanges.subscribe(value => this.checkValue(this.month, value, this.yearElement));
    this.year.valueChanges.subscribe(value => this.checkValue(this.year, value, null, this.MAX_YEAR_SYMBOLS));
  }

  checkValue(field: AbstractControl, value: string, nextField: ElementRef, limit: number = this.MAX_DAY_MONTH_SYMBOLS): void {
    if (value === null || value === '' || typeof value !== 'string') {
      this.form.updateValueAndValidity();
      this.writeValue('');
      return;
    }

    if (value !== value.replace(/\D/g, '')) {
      field.setValue(value.replace(/\D/g, ''));
      this.form.updateValueAndValidity();
    }

    if (value.toString().length === limit && nextField) {
      nextField.nativeElement.focus();
    }

    this.form.updateValueAndValidity();
    this.writeValue();
  }

  writeValue(value?: string): void {
    if (value && typeof value === 'string' && value.length > 0 && moment(value).isValid()) {
      const momentDate = moment(value);
      this.day.setValue(momentDate.format('DD'));
      this.month.setValue(momentDate.format('MM'));
      this.year.setValue(momentDate.format('YYYY'));
      this.form.updateValueAndValidity();
    }

    if (this.validDate) {
      const date =  moment(`${this.year.value}-${this.month.value}-${this.day.value}`).toISOString();
      this.onChange(date);
      this.onTouch(date);
    } else {
      this.onChange('');
      this.onTouch('');
    }
  }

  onChange: any = () => {};
  onTouch: any = () => {};
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouch = fn; }
}
